import { addMonths, format, parseISO } from "date-fns";
import type {
  Asset,
  AssetStatus,
  AssetType,
  ComplianceChoice,
  Deployment,
  Dossier,
  ExtensionChoice,
  PolePresence,
  ReservationRequest,
  V2Comment,
  V2Document,
  V2Task,
} from "@/v2/shared/model";
import {
  DEPLOYMENT_TASK_LABELS,
  DOSSIER_TASK_LABELS,
  REQUEST_TASK_LABELS,
  SEED_ASSETS,
  SEED_REQUESTS,
} from "@/v2/data/seed";
import { findV2UserById } from "@/v2/auth/users";
import { TODAY, fullName } from "@/v2/shared/format";

// In-memory mock store. Vervangt de backend; alle reads/writes lopen hierlangs
// met een kunstmatige vertraging zodat loading states zichtbaar zijn.
let assets: Array<Asset> = SEED_ASSETS.map((a) => ({ ...a }));
let requests: Array<ReservationRequest> = SEED_REQUESTS.map((r) => ({ ...r }));
// Boven het hoogste geseede nummer (r-9209), zodat nieuwe records nooit een
// bestaand id of aanvraagnummer hergebruiken.
let sequence = 9209;

function delay<T>(value: T, ms = 250): Promise<T> {
  return new Promise((resolve) => setTimeout(() => resolve(value), ms));
}

function newId(prefix: string): string {
  sequence += 1;
  return `${prefix}-${sequence}`;
}

function nowIso(): string {
  return new Date().toISOString();
}

function periodsOverlap(
  aStart: string,
  aEnd: string,
  bStart: string,
  bEnd: string,
): boolean {
  return aStart <= bEnd && bStart <= aEnd;
}

function replaceRequest(updated: ReservationRequest): ReservationRequest {
  requests = requests.map((r) => (r.id === updated.id ? updated : r));
  return updated;
}

function getRequestOrThrow(id: string): ReservationRequest {
  const found = requests.find((r) => r.id === id);
  if (!found) throw new Error(`Aanvraag niet gevonden: ${id}`);
  return found;
}

// CB-17: vaste takenlijst instantiëren voor een nieuw(e) aanvraag/dossier/inzet.
function makeTasks(labels: ReadonlyArray<string>): Array<V2Task> {
  return labels.map((label) => ({ id: newId("t"), label, done: false }));
}

function makeComment(text: string, author: string, kind: V2Comment["kind"]): V2Comment {
  return { id: newId("c"), author, createdAt: nowIso(), text, kind };
}

// CB-25: automatische systeemmails naar de aanvrager (gemockt — er vertrekt
// niets; de mail verschijnt als entry in de communicatietab).
const SYSTEM_AUTHOR = "Systeem (automatische mail)";

function withSystemMail(
  request: ReservationRequest,
  subject: string,
  body: string,
): ReservationRequest {
  return {
    ...request,
    communications: [
      ...request.communications,
      {
        id: newId("m"),
        direction: "outgoing",
        subject,
        body,
        at: nowIso(),
        author: SYSTEM_AUTHOR,
      },
    ],
  };
}

function logHistory(
  request: ReservationRequest,
  author: string,
  action: string,
): ReservationRequest {
  return {
    ...request,
    history: [
      ...request.history,
      { id: newId("h"), at: nowIso(), author, action },
    ],
  };
}

function updateDossier(
  request: ReservationRequest,
  dossierId: string,
  fn: (dossier: Dossier) => Dossier,
): ReservationRequest {
  return {
    ...request,
    dossiers: request.dossiers.map((d) => (d.id === dossierId ? fn(d) : d)),
  };
}

function updateDeployment(
  request: ReservationRequest,
  dossierId: string,
  deploymentId: string,
  fn: (deployment: Deployment) => Deployment,
): ReservationRequest {
  return updateDossier(request, dossierId, (dossier) => ({
    ...dossier,
    deployments: dossier.deployments.map((dep) =>
      dep.id === deploymentId ? fn(dep) : dep,
    ),
  }));
}

function getDossierOrThrow(request: ReservationRequest, dossierId: string): Dossier {
  const dossier = request.dossiers.find((d) => d.id === dossierId);
  if (!dossier) throw new Error(`Dossier niet gevonden: ${dossierId}`);
  return dossier;
}

// ── Reads ───────────────────────────────────────────────────────────────────
export async function fetchAssets(): Promise<ReadonlyArray<Asset>> {
  return delay(assets);
}

export async function fetchRequests(): Promise<ReadonlyArray<ReservationRequest>> {
  return delay(requests.filter((r) => !r.archived));
}

export async function fetchRequestsByRequester(
  requesterId: string,
): Promise<ReadonlyArray<ReservationRequest>> {
  return delay(requests.filter((r) => !r.archived && r.requesterId === requesterId));
}

export async function fetchRequestById(
  id: string,
): Promise<ReservationRequest | null> {
  return delay(requests.find((r) => r.id === id) ?? null);
}

// Dossier-detail: één dossier met zijn bovenliggende aanvraag. Het dossier
// staat los van de aanvraag maar hangt er wel onder (voor context/nummer).
export type DossierWithRequest = { dossier: Dossier; request: ReservationRequest };

export async function fetchDossierById(id: string): Promise<DossierWithRequest | null> {
  for (const request of requests) {
    if (request.archived) continue;
    const dossier = request.dossiers.find((d) => d.id === id);
    if (dossier) return delay({ dossier, request });
  }
  return delay(null);
}

// Alle actieve (niet-afgeronde) inzetten van een asset, over alle aanvragen.
function blockingDeployments(assetId: string): Array<Deployment> {
  return requests
    .filter((r) => !r.archived)
    .flatMap((r) => r.dossiers)
    .flatMap((d) => d.deployments)
    .filter((dep) => dep.assetId === assetId && dep.status !== "CLOSED");
}

// Beschikbaarheid per periode (CB-31): assets met status AVAILABLE zonder
// geplande/lopende inzet in de gevraagde periode; hetzelfde asset kan nooit
// dubbel geboekt worden. Optioneel gefilterd op type.
export async function fetchAvailableAssets(
  startDate: string,
  endDate: string,
  type?: AssetType,
): Promise<ReadonlyArray<Asset>> {
  const available = assets.filter(
    (a) =>
      a.status === "AVAILABLE" &&
      (type ? a.type === type : true) &&
      !blockingDeployments(a.id).some((dep) =>
        periodsOverlap(dep.expectedStart, dep.expectedEnd, startDate, endDate),
      ),
  );
  return delay(available);
}

export function findAsset(id: string | undefined): Asset | undefined {
  return id ? assets.find((a) => a.id === id) : undefined;
}

// ── Aanvraag-mutaties ─────────────────────────────────────────────────────────
export type CreateRequestInput = Omit<
  ReservationRequest,
  | "id"
  | "number"
  | "status"
  | "createdAt"
  | "archived"
  | "dossiers"
  | "comments"
  | "tasks"
  | "history"
  | "communications"
  | "documents"
> & { documents?: ReservationRequest["documents"] };

export async function createRequest(
  input: CreateRequestInput,
): Promise<ReservationRequest> {
  const id = newId("r");
  const number = `CB-2026-${sequence}`;
  const author = `${input.requesterFirstName} ${input.requesterLastName}`;
  const created: ReservationRequest = {
    ...input,
    id,
    number,
    status: "OPEN",
    createdAt: nowIso(),
    archived: false,
    dossiers: [],
    comments: [],
    tasks: makeTasks(REQUEST_TASK_LABELS),
    documents: input.documents ?? [],
    communications: [],
    history: [{ id: newId("h"), at: nowIso(), author, action: "Aanvraag ingediend" }],
  };
  const withConfirmation = withSystemMail(
    created,
    `Bevestiging van je aanvraag ${number}`,
    `Beste ${input.requesterFirstName}, je aanvraag "${created.title}" is goed ontvangen en heeft de status Open. Reken op minimaal 8 werkdagen tussen indiening en effectieve plaatsing.`,
  );
  requests = [withConfirmation, ...requests];
  return delay(withConfirmation, 350);
}

// CB-10/CB-15: alles is een opmerking op de aanvraag. Mailen naar de
// aanvrager is een bewuste keuze via een vinkje (geen automatisme): dan wordt
// de opmerking gemarkeerd als terugkoppeling ("question") en gemockt gemaild.
// Zonder vinkje is het een interne notitie of een geplakte binnengekomen
// mail. De beheerder die plaatst, wordt (indien nog niet gezet) behandelaar.
export async function addRequestComment(
  id: string,
  text: string,
  author: string,
  sendMail: boolean,
  handlerId?: string,
): Promise<ReservationRequest> {
  const current = getRequestOrThrow(id);
  const updated = logHistory(
    {
      ...current,
      handlerId: current.handlerId ?? handlerId,
      comments: [...current.comments, makeComment(text, author, sendMail ? "question" : "note")],
    },
    author,
    sendMail ? "Opmerking geplaatst en per mail naar de aanvrager verzonden" : "Opmerking geplaatst",
  );
  return delay(replaceRequest(updated), 150);
}

// Interne opmerking op het dossier (CB-15): enkel zichtbaar voor beheerders,
// in de feed van het dossierdetail. Opmerkingen op de aanvraag (hierboven)
// zijn zichtbaar voor aanvrager én beheerder.
export async function addDossierComment(
  requestId: string,
  dossierId: string,
  text: string,
  author: string,
): Promise<ReservationRequest> {
  const current = getRequestOrThrow(requestId);
  getDossierOrThrow(current, dossierId);
  const updated = logHistory(
    updateDossier(current, dossierId, (dossier) => ({
      ...dossier,
      comments: [...dossier.comments, makeComment(text, author, "note")],
    })),
    author,
    "Interne opmerking geplaatst op het dossier",
  );
  return delay(replaceRequest(updated), 150);
}

// De velden van een aanvraag die de beheerder achteraf mag corrigeren. Het
// aanvraagnummer, de status, de behandeling en de gekoppelde collecties
// (dossiers, historiek, ...) staan hier bewust niet in: die volgen uit het
// proces, niet uit een correctie.
export type UpdateRequestInput = {
  title: string;
  requesterLastName: string;
  requesterFirstName: string;
  requesterEmail: string;
  requesterPhone: string;
  requesterService: string;
  requesterServiceContact: string;
  startDate: string;
  startTime?: string;
  endDate: string;
  extension: ExtensionChoice;
  address: string;
  locationLat: number;
  locationLng: number;
  locationDescription: string;
  polePresence: PolePresence;
  environmentalFactors: string;
  event: string;
  eventInfo: string;
  parkingSigns: string;
  reason: string;
  pvNumber: string;
  specialAuthorization: string;
  addedValue: string;
  compliance: ComplianceChoice;
};

// CB-1: aanvraag corrigeren door de beheerder. De toegekende periode van
// bestaande dossiers verandert hier niet mee (die staat los van de aanvraag).
// Een afgesloten aanvraag kan niet meer bewerkt worden.
export async function updateRequest(
  id: string,
  input: UpdateRequestInput,
  author: string,
): Promise<ReservationRequest> {
  const current = getRequestOrThrow(id);
  if (current.status === "CLOSED") {
    throw new Error("Een afgesloten aanvraag kan niet meer bewerkt worden.");
  }
  if (input.endDate <= input.startDate) {
    throw new Error("De einddatum moet na de begindatum liggen.");
  }
  const updated = logHistory({ ...current, ...input }, author, "Aanvraaggegevens aangepast");
  return delay(replaceRequest(updated), 200);
}

export type PlanDeploymentInput = {
  assetId: string;
  expectedStart: string;
  expectedEnd: string;
  externalFirm?: string;
};

// CB-12: aanvaarden — de aanvraag wordt "Aanvaard" en er ontstaat een dossier
// (los van de aanvraag) met de toegekende periode. Er wordt bij aanvaarding
// nog géén asset ingepland; het dossier start op de wachtrij en blijft daar
// tot er later een inzet (deployment) gepland wordt.
export async function acceptRequest(
  id: string,
  startDate: string,
  endDate: string,
  author: string,
  handlerId?: string,
): Promise<ReservationRequest> {
  const current = getRequestOrThrow(id);
  const dossier: Dossier = {
    id: newId("dos"),
    requestId: id,
    startDate,
    endDate,
    status: "ON_WAITLIST",
    extensionCount: 0,
    comments: [],
    tasks: makeTasks(DOSSIER_TASK_LABELS),
    deployments: [],
  };
  let updated: ReservationRequest = withSystemMail(
    {
      ...current,
      status: "ACCEPTED",
      handlerId: current.handlerId ?? handlerId,
      dossiers: [...current.dossiers, dossier],
    },
    `Aanvraag ${current.number} aanvaard`,
    `Beste ${current.requesterFirstName}, je aanvraag "${current.title}" is aanvaard. Het dossier staat op de wachtrij tot er een inzet ingepland wordt.`,
  );
  updated = logHistory(updated, author, "Aanvraag aanvaard, dossier aangemaakt (op wachtrij)");
  return delay(replaceRequest(updated), 200);
}

// CB-16/CB-39: inzet plannen — koppelt één asset aan het dossier voor een
// verwachte plaatsingsperiode. Guard: hetzelfde asset nooit dubbel geboekt.
function planDeploymentOn(
  request: ReservationRequest,
  dossierId: string,
  input: PlanDeploymentInput,
  author: string,
): ReservationRequest {
  const asset = findAsset(input.assetId);
  if (!asset) throw new Error(`Asset niet gevonden: ${input.assetId}`);
  const doubleBooked = blockingDeployments(input.assetId).some((dep) =>
    periodsOverlap(dep.expectedStart, dep.expectedEnd, input.expectedStart, input.expectedEnd),
  );
  if (doubleBooked) {
    throw new Error(`Asset ${asset.number} is al ingeboekt in (een deel van) die periode.`);
  }
  const deployment: Deployment = {
    id: newId("dep"),
    dossierId,
    assetId: input.assetId,
    expectedStart: input.expectedStart,
    expectedEnd: input.expectedEnd,
    externalFirm: input.externalFirm,
    status: "PLANNED",
    comments: [],
    tasks: makeTasks(DEPLOYMENT_TASK_LABELS),
  };
  const plannedStatus: Dossier["status"] = "PLANNED";
  const withDeployment = updateDossier(request, dossierId, (dossier) => ({
    ...dossier,
    status: plannedStatus,
    deployments: [...dossier.deployments, deployment],
  }));
  return logHistory(
    withDeployment,
    author,
    `Inzet gepland met asset ${asset.number}${input.externalFirm ? ` (${input.externalFirm})` : ""}`,
  );
}

export async function planDeployment(
  requestId: string,
  dossierId: string,
  input: PlanDeploymentInput,
  author: string,
): Promise<ReservationRequest> {
  const current = getRequestOrThrow(requestId);
  getDossierOrThrow(current, dossierId);
  return delay(replaceRequest(planDeploymentOn(current, dossierId, input, author)), 200);
}

// CB-22: verlenging — standaard één maand, maximaal twee verlengingen.
export async function extendDossier(
  requestId: string,
  dossierId: string,
  author: string,
): Promise<ReservationRequest> {
  const current = getRequestOrThrow(requestId);
  const dossier = getDossierOrThrow(current, dossierId);
  if (current.extension !== "possible") {
    throw new Error("Deze aanvraag heeft een vaste einddatum. Verlenging is niet voorzien.");
  }
  if (dossier.extensionCount >= 2) {
    throw new Error("Maximaal twee verlengingen bereikt. Het dossier kan niet verder verlengd worden.");
  }
  const newEnd = format(addMonths(parseISO(dossier.endDate), 1), "yyyy-MM-dd");
  const updated = logHistory(
    updateDossier(current, dossierId, (d) => ({
      ...d,
      endDate: newEnd,
      extensionCount: d.extensionCount + 1,
    })),
    author,
    `Dossier verlengd tot ${format(parseISO(newEnd), "dd/MM/yyyy")} (verlenging ${dossier.extensionCount + 1} van 2)`,
  );
  return delay(replaceRequest(updated), 150);
}

// Een dossier "loopt" zodra de toegekende periode begonnen is of er iets
// geplaatst is; dan is vrij aanpassen van de periode niet meer veilig.
export function isDossierRunning(dossier: Dossier): boolean {
  if (dossier.status === "CLOSED") return true;
  const started = dossier.startDate <= TODAY;
  const placed = dossier.deployments.some(
    (d) => d.status === "DEPLOYED" || d.status === "CLOSED",
  );
  return started || placed;
}

// Vrij corrigeren van de toegekende periode — enkel zolang het dossier nog
// niet lopend is (zie isDossierRunning). Verlengen (CB-22) neemt het daarna
// over binnen de verlengingsregels.
export async function updateDossierPeriod(
  requestId: string,
  dossierId: string,
  startDate: string,
  endDate: string,
  author: string,
): Promise<ReservationRequest> {
  const current = getRequestOrThrow(requestId);
  const dossier = getDossierOrThrow(current, dossierId);
  if (isDossierRunning(dossier)) {
    throw new Error("De periode kan enkel aangepast worden zolang het dossier nog niet lopend is.");
  }
  if (endDate <= startDate) {
    throw new Error("De einddatum moet na de begindatum liggen.");
  }
  const updated = logHistory(
    updateDossier(current, dossierId, (d) => ({ ...d, startDate, endDate })),
    author,
    `Toegekende periode aangepast naar ${format(parseISO(startDate), "dd/MM/yyyy")} - ${format(parseISO(endDate), "dd/MM/yyyy")}`,
  );
  return delay(replaceRequest(updated), 150);
}

// CB-16: geplande inzet verwijderen — enkel zolang er geen effectieve
// plaatsing geregistreerd is. Laatste inzet weg → dossier terug op wachtrij.
export async function removeDeployment(
  requestId: string,
  dossierId: string,
  deploymentId: string,
  author: string,
): Promise<ReservationRequest> {
  const current = getRequestOrThrow(requestId);
  const dossier = getDossierOrThrow(current, dossierId);
  const deployment = dossier.deployments.find((dep) => dep.id === deploymentId);
  if (!deployment) throw new Error(`Inzet niet gevonden: ${deploymentId}`);
  if (deployment.status !== "PLANNED") {
    throw new Error("Een geplaatste of afgeronde inzet kan niet verwijderd worden.");
  }
  const asset = findAsset(deployment.assetId);
  const remaining = dossier.deployments.filter((dep) => dep.id !== deploymentId);
  const waitlistStatus: Dossier["status"] = "ON_WAITLIST";
  const updated = logHistory(
    updateDossier(current, dossierId, (d) => ({
      ...d,
      deployments: remaining,
      status: remaining.length === 0 ? waitlistStatus : d.status,
    })),
    author,
    `Inzet verwijderd (asset ${asset?.number ?? "?"})${remaining.length === 0 ? ", dossier terug op wachtrij" : ""}`,
  );
  return delay(replaceRequest(updated), 150);
}

// CB-16: asset van een inzet vervangen met reden (ook ander type).
export async function replaceDeploymentAsset(
  requestId: string,
  dossierId: string,
  deploymentId: string,
  newAssetId: string,
  reason: string,
  author: string,
): Promise<ReservationRequest> {
  const current = getRequestOrThrow(requestId);
  const newAsset = findAsset(newAssetId);
  const withReplacement = updateDeployment(current, dossierId, deploymentId, (dep) => ({
    ...dep,
    assetId: newAssetId,
    comments: [...dep.comments, makeComment(`Asset vervangen: ${reason}`, author, "note")],
  }));
  const updated = logHistory(
    withReplacement,
    author,
    `Asset van inzet vervangen door ${newAsset?.number ?? "?"}: ${reason}`,
  );
  return delay(replaceRequest(updated), 200);
}

// CB-39: effectieve plaatsing registreren — de inzet gaat naar "Geplaatst".
export async function markDeployed(
  requestId: string,
  dossierId: string,
  deploymentId: string,
  actualStart: string,
  executedBy: string,
  author: string,
): Promise<ReservationRequest> {
  const current = getRequestOrThrow(requestId);
  const deployedStatus: Deployment["status"] = "DEPLOYED";
  const updated = logHistory(
    updateDeployment(current, dossierId, deploymentId, (dep) => ({
      ...dep,
      status: deployedStatus,
      actualStart,
      executedBy,
    })),
    author,
    `Plaatsing geregistreerd (uitgevoerd door ${executedBy})`,
  );
  return delay(replaceRequest(updated), 200);
}

// CB-39: ophaling — effectieve einddatum wordt geregistreerd, inzet afgerond.
export async function completeDeployment(
  requestId: string,
  dossierId: string,
  deploymentId: string,
  actualEnd: string,
  author: string,
): Promise<ReservationRequest> {
  const current = getRequestOrThrow(requestId);
  const closedStatus: Deployment["status"] = "CLOSED";
  const updated = logHistory(
    updateDeployment(current, dossierId, deploymentId, (dep) => ({
      ...dep,
      status: closedStatus,
      actualEnd,
    })),
    author,
    "Inzet afgerond (ophaling geregistreerd)",
  );
  return delay(replaceRequest(updated), 200);
}

// CB-37: afsluiten, ook zonder dossier of inzet; onderliggende dossiers
// sluiten mee af.
export async function closeRequest(
  id: string,
  author: string,
): Promise<ReservationRequest> {
  const current = getRequestOrThrow(id);
  const closedStatus: Dossier["status"] = "CLOSED";
  const updated = logHistory(
    withSystemMail(
      {
        ...current,
        status: "CLOSED",
        dossiers: current.dossiers.map((d) => ({ ...d, status: closedStatus })),
      },
      `Aanvraag ${current.number} afgesloten`,
      `Beste ${current.requesterFirstName}, je aanvraag "${current.title}" is afgesloten.`,
    ),
    author,
    "Aanvraag afgesloten",
  );
  return delay(replaceRequest(updated), 150);
}

// CB-38: soft delete.
export async function archiveRequest(
  id: string,
  author: string,
): Promise<ReservationRequest> {
  const current = getRequestOrThrow(id);
  const updated = logHistory({ ...current, archived: true }, author, "Aanvraag verwijderd");
  return delay(replaceRequest(updated), 150);
}

// ── Taken (CB-17) — afvinken met auteur en tijdstip ───────────────────────────
export type TaskTarget =
  | { level: "request" }
  | { level: "dossier"; dossierId: string }
  | { level: "deployment"; dossierId: string; deploymentId: string };

function toggleTaskIn(tasks: ReadonlyArray<V2Task>, taskId: string, author: string): {
  tasks: Array<V2Task>;
  toggled: V2Task | undefined;
} {
  let toggled: V2Task | undefined;
  const next = tasks.map((t) => {
    if (t.id !== taskId) return t;
    toggled = t.done
      ? { ...t, done: false, doneBy: undefined, doneAt: undefined }
      : { ...t, done: true, doneBy: author, doneAt: nowIso() };
    return toggled;
  });
  return { tasks: next, toggled };
}

export async function toggleTask(
  requestId: string,
  target: TaskTarget,
  taskId: string,
  author: string,
): Promise<ReservationRequest> {
  const current = getRequestOrThrow(requestId);
  let updated = current;
  let toggled: V2Task | undefined;
  if (target.level === "request") {
    const result = toggleTaskIn(current.tasks, taskId, author);
    toggled = result.toggled;
    updated = { ...current, tasks: result.tasks };
  } else if (target.level === "dossier") {
    updated = updateDossier(current, target.dossierId, (d) => {
      const result = toggleTaskIn(d.tasks, taskId, author);
      toggled = result.toggled;
      return { ...d, tasks: result.tasks };
    });
  } else {
    updated = updateDeployment(current, target.dossierId, target.deploymentId, (dep) => {
      const result = toggleTaskIn(dep.tasks, taskId, author);
      toggled = result.toggled;
      return { ...dep, tasks: result.tasks };
    });
  }
  if (toggled?.done) {
    updated = logHistory(updated, author, `Taak afgevinkt: ${toggled.label}`);
  }
  return delay(replaceRequest(updated), 120);
}

// CB-17: optionele herinnering (datum, eventueel tijdstip) op een taak
// instellen of verwijderen, optioneel gericht aan één beheerder; zonder
// persoon geldt de melding voor het hele team (notificatiebel, PZAO-41).
export async function setTaskReminder(
  requestId: string,
  target: TaskTarget,
  taskId: string,
  reminderDate: string | undefined,
  reminderTime: string | undefined,
  reminderAssigneeId: string | undefined,
  author: string,
): Promise<ReservationRequest> {
  const current = getRequestOrThrow(requestId);
  let changed: V2Task | undefined;
  const apply = (tasks: ReadonlyArray<V2Task>): Array<V2Task> =>
    tasks.map((t) => {
      if (t.id !== taskId) return t;
      changed = {
        ...t,
        reminderDate,
        reminderTime: reminderDate ? reminderTime : undefined,
        reminderAssigneeId: reminderDate ? reminderAssigneeId : undefined,
      };
      return changed;
    });

  let updated = current;
  if (target.level === "request") {
    updated = { ...current, tasks: apply(current.tasks) };
  } else if (target.level === "dossier") {
    updated = updateDossier(current, target.dossierId, (d) => ({ ...d, tasks: apply(d.tasks) }));
  } else {
    updated = updateDeployment(current, target.dossierId, target.deploymentId, (dep) => ({
      ...dep,
      tasks: apply(dep.tasks),
    }));
  }
  if (!changed) throw new Error(`Taak niet gevonden: ${taskId}`);
  const assignee = reminderAssigneeId ? findV2UserById(reminderAssigneeId) : undefined;
  const audience = assignee
    ? `gericht aan ${fullName(assignee.firstName, assignee.lastName)}`
    : "voor het hele team";
  const when = reminderDate
    ? `${format(parseISO(reminderDate), "dd/MM/yyyy")}${reminderTime ? ` om ${reminderTime}` : ""}`
    : undefined;
  updated = logHistory(
    updated,
    author,
    when
      ? `Herinnering ingesteld op taak "${changed.label}" (${when}, ${audience})`
      : `Herinnering verwijderd van taak "${changed.label}"`,
  );
  return delay(replaceRequest(updated), 120);
}

// ── Documenten (CB-21) — bijkomend document toevoegen ────────────────────────
export async function addDocument(
  requestId: string,
  filename: string,
  sizeLabel: string,
  author: string,
): Promise<ReservationRequest> {
  const current = getRequestOrThrow(requestId);
  const document: V2Document = { id: newId("d"), filename, kind: "other", sizeLabel };
  const updated = logHistory(
    { ...current, documents: [...current.documents, document] },
    author,
    `Document toegevoegd: ${filename}`,
  );
  return delay(replaceRequest(updated), 200);
}

// ── Assets (CB-29/CB-33) ─────────────────────────────────────────────────────
export async function setAssetStatus(
  assetId: string,
  status: AssetStatus,
): Promise<Asset> {
  assets = assets.map((a) => (a.id === assetId ? { ...a, status } : a));
  const updated = assets.find((a) => a.id === assetId);
  if (!updated) throw new Error(`Asset niet gevonden: ${assetId}`);
  return delay(updated, 150);
}

// CB-29: Excel-oplading (gemockt): herhaald opladen werkt bijwerkend —
// bestaande nummers worden geüpdatet, nieuwe toegevoegd. Het bestand zelf
// wordt niet gelezen; de import is gesimuleerd.
export type ExcelImportResult = { added: number; updated: number };

const MOCK_IMPORT_ROWS: ReadonlyArray<Pick<Asset, "number" | "type">> = [
  { number: "CAM-011", type: "camcar" },
  { number: "CAM-012", type: "camtower" },
];

export async function importAssetsFromExcel(): Promise<ExcelImportResult> {
  let added = 0;
  const updated = assets.length;
  for (const row of MOCK_IMPORT_ROWS) {
    if (!assets.some((a) => a.number === row.number)) {
      assets = [
        ...assets,
        {
          id: newId("a"),
          number: row.number,
          type: row.type,
          status: "AVAILABLE",
          comments: [],
        },
      ];
      added += 1;
    }
  }
  return delay({ added, updated }, 400);
}
